import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { pt } from "date-fns/locale";
import { format, isAfter, isBefore, parseISO, startOfToday } from "date-fns";
import { CalendarDays, X, Eraser } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { formatDate } from "@/hooks/useQuote";

const iso = (d) => format(d, "yyyy-MM-dd");

const isBookedNight = (booked, day) =>
  booked.some((r) => {
    const d = iso(day);
    return d >= r.check_in && d < r.check_out;
  });

const hasConflict = (booked, fromIso, toIso) =>
  booked.some((r) => fromIso < r.check_out && toIso > r.check_in);

export const DateRangeField = ({
  booked = [],
  bookedMatchers = [],
  value,
  onChange,
  testId = "date-range",
  triggerClassName = "",
  nights = 0,
}) => {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(1);
  const from = value.checkIn ? parseISO(value.checkIn) : undefined;
  const to = value.checkOut ? parseISO(value.checkOut) : undefined;

  useEffect(() => {
    const update = () => setMonths(window.innerWidth < 640 ? 1 : 2);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const pick = (day, modifiers) => {
    if (modifiers?.disabled) return;
    const dayIso = iso(day);
    const startNew = () => {
      if (isBookedNight(booked, day)) return;
      onChange({ checkIn: dayIso, checkOut: "" });
    };
    if (!value.checkIn || value.checkOut) return startNew();
    if (!isAfter(day, parseISO(value.checkIn))) return startNew();
    if (hasConflict(booked, value.checkIn, dayIso)) return startNew();
    onChange({ checkIn: value.checkIn, checkOut: dayIso });
  };

  const label = value.checkIn
    ? `${formatDate(value.checkIn)}${value.checkOut ? ` → ${formatDate(value.checkOut)}` : " → saída"}`
    : "Escolher datas";

  return (
    <>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen(true)}
        className={`flex w-full min-w-0 items-center gap-2.5 text-left ${triggerClassName}`}
      >
        <CalendarDays size={16} className="shrink-0 text-clay" />
        <span className="min-w-0 flex-1 truncate">
          <span className="block text-[11px] font-semibold text-ink/70">Datas</span>
          <span className={`block truncate text-[15px] ${value.checkIn ? "text-ink" : "text-fog/70"}`}>{label}</span>
        </span>
        {nights > 0 && (
          <span className="shrink-0 rounded-full bg-sand px-2.5 py-1 text-[11px] font-bold text-clay" data-testid={`${testId}-nights`}>
            {nights} {nights === 1 ? "noite" : "noites"}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`${testId}-panel`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center"
          >
            <motion.div
              initial={{ y: 60, opacity: 0.6 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92svh] w-full overflow-y-auto rounded-t-3xl bg-white px-4 pb-5 pt-4 text-ink sm:mx-4 sm:max-w-fit sm:rounded-3xl sm:px-6 sm:pb-6"
            >
              <div className="sticky top-0 -mx-4 mb-2 flex items-center justify-between gap-3 bg-white px-4 pb-3 sm:-mx-6 sm:px-6">
                <div className="min-w-0">
                  <p className="font-display text-base font-extrabold tracking-[-0.02em]">
                    {value.checkIn && !value.checkOut ? "Escolha a data de saída" : "Escolha as suas datas"}
                  </p>
                  <p className="truncate text-xs text-fog">Os dias riscados já estão ocupados</p>
                </div>
                <button
                  type="button"
                  data-testid={`${testId}-close`}
                  onClick={() => setOpen(false)}
                  className="shrink-0 rounded-full border border-line p-2 text-ink transition-colors duration-300 hover:bg-sand"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex min-w-0 justify-center">
                <Calendar
                  mode="range"
                  locale={pt}
                  numberOfMonths={months}
                  fromMonth={startOfToday()}
                  defaultMonth={from || startOfToday()}
                  selected={{ from, to }}
                  onDayClick={pick}
                  disabled={{ before: startOfToday() }}
                  modifiers={{ booked: bookedMatchers }}
                  modifiersClassNames={{ booked: "day-booked" }}
                  className="p-0"
                  classNames={{
                    months: "flex flex-col gap-6 sm:flex-row sm:gap-8",
                    caption_label: "font-display text-sm font-bold capitalize text-ink",
                    head_cell: "w-10 text-[11px] font-medium uppercase tracking-wider text-fog",
                    cell: "p-0 text-center text-sm",
                    day: "h-10 w-10 rounded-full text-sm text-ink hover:bg-sand",
                    day_selected: "bg-clay text-white hover:bg-clay hover:text-white focus:bg-clay",
                    day_range_middle: "aria-selected:bg-sand aria-selected:text-ink rounded-none",
                    day_today: "font-bold text-clay",
                    day_disabled: "text-fog/35",
                    day_outside: "text-fog/30",
                  }}
                />
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 border-t border-line pt-4">
                <button
                  type="button"
                  data-testid={`${testId}-clear`}
                  onClick={() => onChange({ checkIn: "", checkOut: "" })}
                  className="inline-flex items-center gap-1.5 text-sm text-fog underline-offset-4 transition-colors duration-300 hover:text-clay hover:underline"
                >
                  <Eraser size={14} /> Limpar
                </button>
                <button
                  type="button"
                  data-testid={`${testId}-confirm`}
                  disabled={!value.checkIn || !value.checkOut}
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-clay px-7 py-3 text-sm font-bold text-white transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-clay-dark disabled:opacity-40"
                >
                  Confirmar datas
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const nightsBetween = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const a = parseISO(checkIn);
  const b = parseISO(checkOut);
  return isBefore(a, b) ? Math.round((b - a) / 86400000) : 0;
};
